<template>
  <b-container>
    <div class="cn23-view-wrap">
      <Stepper
        :steps="cn23Steps"
        :activeStepIndex="activeStepIndex"
        :handleStepClick="goToStep"
      >
        <div slot="stepper-content" class="cn-23-form-wrap">
          <div class="step-wrap" v-if="activeStepIndex === 0">
            <transition-group name="fade" tag="div" style="max-width: 100%">
              <CN23ContentItem
                v-for="(item, index) in items"
                :key="`item-${index}`"
                :index="index"
                :$v="$v.items.$each.$iter[index]"
                :item="item"
                :hasFormError="hasFormError"
                @delete-item="deleteItem(index)"
                :is-last-item="index === items.length - 1"
                @add-item="addItem(index)"
              ></CN23ContentItem>
            </transition-group>
            <CN23ValueSummary
              :totalValue="totalValue"
              :totalWeight="totalWeight"
              @change="changeSummaryValue"
            />
          </div>
          <div class="step-wrap" v-if="activeStepIndex === 1">
            <transition name="fade">
              <Alert
                v-if="parsedTotalValue <= 425 && !prefersCN23"
                class="alert"
                :heading="$t('You may not need a CN23 form')"
                :text="
                  $t(
                    'For shipments with a value of up to €425, filling in the origin and destination address is usually not necessary. Would you like to skip this, or fill in anyway?'
                  )
                "
                :cancelLabel="$t('Fill in anyway')"
                :confirmLabel="$t('Skip')"
                :handleConfirm="goToDownloadForm"
                :handleCancel="enableCN23Form"
              />
            </transition>
            <div
              class="form-section"
              :class="{
                'form-section-disabled':
                  parsedTotalValue <= 425 && !prefersCN23,
              }"
            >
              <b-form-group
                :label="`${$t('Full name')} *`"
                label-for="originFullName"
              >
                <b-form-input
                  class="custom-input"
                  id="originFullName"
                  v-model="$v.origin.fullName.$model"
                  :state="
                    $v.origin.fullName.$dirty || hasFormError
                      ? !$v.origin.fullName.$invalid
                      : null
                  "
                  trim
                  type="text"
                  min="1"
                ></b-form-input>
              </b-form-group>
              <b-form-group
                :label="`${$t('Business name')} *`"
                label-for="originBusinessName"
              >
                <b-form-input
                  class="custom-input"
                  id="originBusinessName"
                  v-model="$v.origin.businessName.$model"
                  :state="
                    $v.origin.businessName.$dirty || hasFormError
                      ? !$v.origin.businessName.$invalid
                      : null
                  "
                  trim
                  type="text"
                  min="1"
                ></b-form-input>
              </b-form-group>

              <b-row>
                <b-col cols="6">
                  <b-form-group
                    :label="`${$t('Address line 1')} *`"
                    label-for="addressLineFirst"
                  >
                    <b-form-input
                      class="custom-input"
                      id="addressLineFirst"
                      v-model="$v.origin.addressLineFirst.$model"
                      :state="
                        $v.origin.addressLineFirst.$dirty || hasFormError
                          ? !$v.origin.addressLineFirst.$invalid
                          : null
                      "
                      trim
                      type="text"
                      min="1"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group
                    :label="$t('Address line 2')"
                    label-for="addressLineSecond"
                  >
                    <b-form-input
                      class="custom-input"
                      id="addressLineSecond"
                      v-model="origin.addressLineSecond"
                      trim
                      type="text"
                      min="1"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="4">
                  <b-form-group
                    :label="`${$t('Postal code')} *`"
                    label-for="postalCode"
                  >
                    <b-form-input
                      class="custom-input"
                      id="postalCode"
                      v-model="$v.origin.postalCode.$model"
                      :state="
                        $v.origin.postalCode.$dirty || hasFormError
                          ? !$v.origin.postalCode.$invalid
                          : null
                      "
                      trim
                      type="text"
                      min="1"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col cols="8">
                  <b-form-group
                    :label="`${$t('City / Town')} *`"
                    label-for="city"
                  >
                    <b-form-input
                      class="custom-input"
                      id="city"
                      v-model="$v.origin.city.$model"
                      :state="
                        $v.origin.city.$dirty || hasFormError
                          ? !$v.origin.city.$invalid
                          : null
                      "
                      trim
                      type="text"
                      min="1"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group :label="$t('Country')" label-for="country">
                <country-select
                  class="input-field country-select"
                  :placeholder="$t('Select country')"
                  v-model="origin.country"
                  :country="origin.country"
                  topCountry="US"
                  :usei18n="false"
                  countryName
                  autocomplete
                />
              </b-form-group>

              <b-form-group :label="$t('Phone number')" label-for="phoneNumber">
                <b-form-input
                  class="custom-input"
                  id="phoneNumber"
                  v-model="origin.phoneNumber"
                  trim
                  type="text"
                  min="1"
                ></b-form-input>
              </b-form-group>

              <b-form-group :label="`${$t('EORI number')} *`" label-for="eori">
                <b-form-input
                  class="custom-input"
                  id="eori"
                  v-model="eori"
                  trim
                  :placeholder="$t('Enter your EORI number')"
                ></b-form-input>
              </b-form-group>
            </div>
          </div>
          <div class="step-wrap" v-if="activeStepIndex === 2">
            <transition name="fade">
              <Alert
                v-if="parsedTotalValue <= 425 && !prefersCN23"
                class="alert"
                :heading="$t('You may not need a CN23 form')"
                :text="
                  $t(
                    'For shipments with a value of up to €425, filling in the origin and destination address is usually not necessary. Would you like to skip this, or fill in anyway?'
                  )
                "
                :cancelLabel="$t('Fill in anyway')"
                :confirmLabel="$t('Skip')"
                :handleConfirm="goToDownloadForm"
                :handleCancel="enableCN23Form"
              />
            </transition>
            <div
              class="form-section"
              :class="{
                'form-section-disabled':
                  parsedTotalValue <= 425 && !prefersCN23,
              }"
            >
              <b-form-group
                :label="`${$t('Full name')} *`"
                label-for="destinationFullName"
              >
                <b-form-input
                  class="custom-input"
                  id="destinationFullName"
                  v-model="$v.destination.fullName.$model"
                  :state="
                    $v.destination.fullName.$dirty || hasFormError
                      ? !$v.destination.fullName.$invalid
                      : null
                  "
                  trim
                  type="text"
                  min="1"
                ></b-form-input>
              </b-form-group>
              <b-form-group
                :label="`${$t('Business name')} *`"
                label-for="destinationBusinessName"
              >
                <b-form-input
                  class="custom-input"
                  id="destinationBusinessName"
                  v-model="$v.destination.businessName.$model"
                  :state="
                    $v.destination.businessName.$dirty || hasFormError
                      ? !$v.destination.businessName.$invalid
                      : null
                  "
                  trim
                  type="text"
                  min="1"
                ></b-form-input>
              </b-form-group>

              <b-row>
                <b-col cols="6">
                  <b-form-group
                    :label="`${$t('Address line 1')} *`"
                    label-for="addressLineFirst"
                  >
                    <b-form-input
                      class="custom-input"
                      id="addressLineFirst"
                      v-model="$v.destination.addressLineFirst.$model"
                      :state="
                        $v.destination.addressLineFirst.$dirty || hasFormError
                          ? !$v.destination.addressLineFirst.$invalid
                          : null
                      "
                      trim
                      type="text"
                      min="1"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group
                    :label="$t('Address line 2')"
                    label-for="addressLineSecond"
                  >
                    <b-form-input
                      class="custom-input"
                      id="addressLineSecond"
                      v-model="destination.addressLineSecond"
                      trim
                      type="text"
                      min="1"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="4">
                  <b-form-group
                    :label="`${$t('Postal code')} *`"
                    label-for="postalCode"
                  >
                    <b-form-input
                      class="custom-input"
                      id="postalCode"
                      v-model="$v.destination.postalCode.$model"
                      :state="
                        $v.destination.postalCode.$dirty || hasFormError
                          ? !$v.destination.postalCode.$invalid
                          : null
                      "
                      trim
                      type="text"
                      min="1"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col cols="8">
                  <b-form-group
                    :label="`${$t('City / Town')} *`"
                    label-for="city"
                  >
                    <b-form-input
                      class="custom-input"
                      id="city"
                      v-model="$v.destination.city.$model"
                      :state="
                        $v.destination.city.$dirty || hasFormError
                          ? !$v.destination.city.$invalid
                          : null
                      "
                      trim
                      type="text"
                      min="1"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group :label="$t('Country')" label-for="country">
                <country-select
                  class="input-field country-select"
                  v-model="destination.country"
                  :country="destination.country"
                  :placeholder="$t('Select country')"
                  topCountry="US"
                  :usei18n="false"
                  countryName
                  autocomplete
                />
              </b-form-group>

              <b-form-group :label="$t('Phone number')" label-for="phoneNumber">
                <b-form-input
                  class="custom-input"
                  id="phoneNumber"
                  v-model="destination.phoneNumber"
                  trim
                  type="text"
                  min="1"
                ></b-form-input>
              </b-form-group>

              <b-form-group
                :label="`${$t('EORI UK number')} *`"
                label-for="eoriUK"
                v-if="destination.country === 'United Kingdom'"
              >
                <b-form-input
                  class="custom-input"
                  id="eoriUK"
                  v-model="eoriUK"
                  trim
                  :placeholder="$t('Enter your EORI number')"
                ></b-form-input>
              </b-form-group>
            </div>
          </div>
          <div class="step-wrap" v-if="activeStepIndex === 3">
            <div class="form-section">
              <h4 class="form-section-heading">
                {{ $t("What's the purpose of this shipment?") }}
              </h4>
              <div class="centered-checkbox-group">
                <b-form-group>
                  <b-form-checkbox-group
                    id="purpose"
                    v-model="categoryOfItem"
                    name="purpose"
                  >
                    <b-form-checkbox value="Gift">
                      {{ $t("Gift") }}
                    </b-form-checkbox>
                    <b-form-checkbox value="Documents">
                      {{ $t("Documents") }}
                    </b-form-checkbox>
                    <b-form-checkbox value="Commercial">
                      {{ $t("Commercial") }}
                    </b-form-checkbox>
                    <b-form-checkbox value="Returned goods">
                      {{ $t("Returned goods") }}
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                </b-form-group>
              </div>
              <b-form-group :label="$t('Comments')">
                <b-form-textarea
                  id="textarea"
                  v-model="comments"
                  :placeholder="$t('Enter comments...')"
                  rows="3"
                  max-rows="6"
                ></b-form-textarea>
              </b-form-group>
              <b-form-group
                :label="$t('Invoice number')"
                label-for="invoiceNumber"
              >
                <b-form-input
                  class="custom-input"
                  id="invoiceNumber"
                  v-model="invoiceNumber"
                  trim
                  :placeholder="$t('Enter an invoice number for this shipment')"
                ></b-form-input>
              </b-form-group>
              <b-form-group
                :label="$t('Office of origin')"
                label-for="officeOfOrigin"
              >
                <b-form-input
                  class="custom-input"
                  id="officeOfOrigin"
                  v-model="officeOfOrigin"
                  trim
                  :placeholder="$t('Enter the office of origin')"
                ></b-form-input>
              </b-form-group>
              <b-form-group :label="$t('Shipping with national carrier')">
                <div class="d-flex">
                  <b-form-group
                    :description="
                      $t(
                        'This determines whether to print a CN22/CN23 or Commercial Invoice. E.g. PostNL for The Netherlands, USPS for United States.'
                      )
                    "
                  >
                    <b-form-radio-group
                      id="national-carrier"
                      v-model="usesNationalCarrier"
                      name="national-carrier"
                    >
                      <b-form-radio value="Yes">{{ $t("Yes") }}</b-form-radio>
                      <b-form-radio value="No">{{ $t("No") }}</b-form-radio>
                      <b-form-radio value="I'm not sure">
                        {{ $t("I'm not sure") }}
                      </b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                </div>
              </b-form-group>
              <b-form-group label="Incoterms *" label-for="incoterms">
                <b-form-input
                  class="custom-input"
                  id="incoterms"
                  v-model="incoterms"
                  :state="
                    $v.incoterms.$dirty || hasFormError
                      ? !$v.incoterms.$invalid
                      : null
                  "
                  trim
                  :placeholder="$t('Enter the incoterms code')"
                ></b-form-input>
              </b-form-group>
              <div class="signature-row">
                <b-row class="signature-wrap">
                  <b-col cols="4">
                    <b-form-group :label="$t('Date')" label-for="date">
                      <b-form-input
                        class="custom-input"
                        id="date"
                        :value="date"
                        trim
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col cols="8">
                    <b-form-group
                      :label="`${$t('Signature of declarant')} *`"
                      label-for="signature"
                    >
                      <div class="signature-pad-wrap">
                        <button
                          v-if="signatureData"
                          class="signature-delete-button"
                          :aria-label="$t('Delete item')"
                          @click="clearSignature"
                        >
                          <font-awesome-icon icon="times" />
                        </button>
                        <span
                          class="signature-placeholder"
                          v-if="showSignaturePlaceholder"
                          >{{ $t("Draw your signature here") }}</span
                        >
                        <VueSignaturePad
                          class="signature-pad"
                          id="signature"
                          width="100%"
                          height="150px"
                          ref="signaturePad"
                          :options="options"
                        />
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
              <h4 class="form-section-heading">
                {{ $t("We're almost there...") }}
              </h4>
              <b-form-group
                :label="`${$t(
                  'How many monthly orders do you receive on average?'
                )} *`"
              >
                <div class="d-flex">
                  <b-form-group>
                    <b-form-radio-group
                      id="order-volume"
                      v-model="orderVolume"
                      name="order-volume"
                    >
                      <b-form-radio value="50">0 - 100</b-form-radio>
                      <b-form-radio value="200">100 - 300</b-form-radio>
                      <b-form-radio value="650">300 - 1000</b-form-radio>
                      <b-form-radio value="1750">1000 - 2500</b-form-radio>
                      <b-form-radio value="3750">2500 - 5000</b-form-radio>
                      <b-form-radio value="7500">5000 - 10000</b-form-radio>
                      <b-form-radio value="10000+"
                        >10000 {{ $t("or more") }}</b-form-radio
                      >
                    </b-form-radio-group>
                  </b-form-group>
                </div>
              </b-form-group>
              <b-form-group
                :label="`${$t('Email address')} *`"
                label-for="emailAddress"
              >
                <b-form-input
                  class="custom-input"
                  id="emailAddress"
                  type="email"
                  v-model="$v.emailAddress.$model"
                  trim
                  :placeholder="$t('Enter your email address')"
                  :state="
                    $v.emailAddress.$dirty || hasFormError
                      ? !$v.emailAddress.$invalid
                      : null
                  "
                ></b-form-input>
              </b-form-group>
              <b-form-group :description="$t('consent-text')">
                <b-form-checkbox
                  name="consent"
                  v-model="hasGivenConsent"
                  :value="true"
                  :state="
                    $v.$anyDirty || hasFormError
                      ? $v.hasGivenConsent.$model
                      : null
                  "
                >
                  {{
                    $t(
                      "I agree to receive communications from Sendcloud and I agree to the"
                    )
                  }}
                  <a
                    href="https://www.sendcloud.com/privacy-policy/"
                    class="privacy-policy-link"
                    target="_blank"
                    >{{ $t("Sendcloud privacy policy") }}</a
                  >.
                </b-form-checkbox>
              </b-form-group>
            </div>
          </div>
        </div>
      </Stepper>
      <b-alert show variant="danger" v-if="hasGeneratingError">
        {{
          $t(
            "Something went wrong while generating your form. 😕 Please try again later."
          )
        }}
      </b-alert>
      <b-alert show variant="danger" v-if="hasFormError">
        {{
          $t(
            "Not all fields were filled correctly. 😕 Please check the fields marked in red and try again."
          )
        }}
      </b-alert>
      <button
        class="continue-button"
        @click="goToStep(activeStepIndex + 1)"
        v-if="activeStepIndex < 3"
      >
        {{ $t("Continue") }}
      </button>
      <button
        class="continue-button"
        @click="downloadForm"
        v-if="activeStepIndex === 3"
      >
        <font-awesome-icon icon="download" v-if="!isGenerating" />
        {{ isGenerating ? $t("Generating...") : $t("Download Form") }}
      </button>
      <small class="disclaimer-text">{{ $t(`disclaimer-text`) }}</small>
    </div>
    <div class="pdf-parent-wrap" ref="pdf-parent-wrap"></div>
  </b-container>
</template>

<script>
import axios from "axios";
import { getCurrentLanguage } from "@/lang/i18n.js";
import Stepper from "@/components/generic/Stepper.vue";
import Alert from "@/components/generic/Alert.vue";
import CN23ContentItem from "@/components/CN23/CN23ContentItem.vue";
import CN23ValueSummary from "@/components/CN23/CN23ValueSummary.vue";
import { BContainer } from "bootstrap-vue";
import moment from "moment";
import Vue from "vue";
import Vuelidate from "vuelidate";
import {
  required,
  requiredIf,
  minLength,
  email,
} from "vuelidate/lib/validators";
Vue.use(Vuelidate);

import {
  createDocDefinition,
  shouldUseFullContent,
} from "@/customs-forms-doc.js";

import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

export default {
  name: "CN23",
  components: {
    Stepper,
    Alert,
    CN23ContentItem,
    CN23ValueSummary,
    BContainer,
  },
  validations: {
    items: {
      required,
      $each: {
        quantity: {
          required,
        },
        value: {
          required,
        },
        weight: {
          required,
        },
        description: {
          required,
          minLength: minLength(2),
        },
      },
    },
    origin: {
      fullName: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      businessName: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      postalCode: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      city: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      country: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      addressLineFirst: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
    },
    destination: {
      fullName: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      businessName: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      postalCode: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      city: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      country: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
      addressLineFirst: {
        required: requiredIf(function () {
          return this.isFullAddressRequired;
        }),
      },
    },
    totalWeight: {
      required,
    },
    totalValue: {
      required,
    },
    categoryOfItem: {
      required,
    },
    date: {
      required,
    },
    incoterms: {
      required,
    },
    eori: {
      required: requiredIf(function () {
        return this.isFullAddressRequired;
      }),
    },
    emailAddress: {
      required,
      email,
    },
    hasGivenConsent: {
      required,
    },
    orderVolume: {
      required,
    },
  },
  data() {
    return {
      activeStepIndex: 0,
      prefersCN23: false,
      usesNationalCarrier: "no",
      isGenerating: false,
      hasFormError: false,
      hasGeneratingError: false,
      selectedCarrier: null,
      cn23Steps: [
        {
          title: this.$t("Content"),
          heading: this.$t("List the content of your shipment"),
          icon: "box",
        },
        {
          title: this.$t("Origin"),
          heading: this.$t("Provide the sender details"),
          icon: "truck-moving",
        },
        {
          title: this.$t("Destination"),
          heading: this.$t("Provide the destination details"),
          icon: "map-pin",
        },
        {
          title: this.$t("Download form"),
          heading: this.$t("Finish and download your form"),
          icon: "file-invoice",
        },
      ],
      items: [
        {
          quantity: 1,
          description: null,
          weight: 1,
          value: null,
          hsCode: null,
          originCountry: null,
        },
      ],
      origin: {
        fullName: null,
        businessName: null,
        addressLineFirst: null,
        addressLineSecond: null,
        postalCode: null,
        city: null,
        phoneNumber: null,
        country: "NL",
        customsReference: null,
      },
      destination: {
        fullName: null,
        businessName: null,
        addressLineFirst: null,
        addressLineSecond: null,
        postalCode: null,
        city: null,
        phoneNumber: null,
        country: "NL",
        customsReference: null,
      },
      totalWeight: null,
      totalValue: null,
      categoryOfItem: ["Commercial"],
      comments: null,
      invoiceNumber: null,
      officeOfOrigin: null,
      incoterms: null,
      eori: null,
      eoriUK: null,
      date: new moment().format("DD-MM-YYYY"),
      showSignaturePlaceholder: true,
      signatureData: null,
      emailAddress: null,
      orderVolume: null,
      hasGivenConsent: false,
      options: {
        penColor: "#122857",
        onBegin: this.hideSignaturePlaceholder,
        onEnd: this.saveSignature,
      },
      pdfUrl: null,
    };
  },
  watch: {
    items: {
      handler() {
        this.totalWeight = Intl.NumberFormat("nl-NL", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(
          this.items
            .map((item) => item.weight * item.quantity)
            .reduce((prev, curr) => prev + curr)
        );
        this.totalValue = Intl.NumberFormat("nl-NL", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(
          this.items
            .map((item) => item.value * item.quantity)
            .reduce((prev, curr) => prev + curr)
        );
      },
      deep: true,
    },
  },
  computed: {
    isFullAddressRequired() {
      return shouldUseFullContent(
        this.prefersCN23,
        this.parsedTotalValue,
        this.usesNationalCarrier
      );
    },
    parsedTotalValue() {
      return parseInt(this.totalValue);
    },
    formData() {
      return {
        items: this.items,
        origin: this.origin,
        destination: this.destination,
        totalWeight: this.totalWeight,
        parsedTotalValue: this.parsedTotalValue,
        totalValue: this.totalValue,
        categoryOfItem: this.categoryOfItem,
        invoiceNumber: this.invoiceNumber,
        officeOfOrigin: this.officeOfOrigin,
        incoterms: this.incoterms,
        eori: this.eori,
        eoriUK: this.eoriUK,
        date: this.date,
        prefersCN23: this.prefersCN23,
        usesNationalCarrier: this.usesNationalCarrier,
        signatureData: this.signatureData,
        comments: this.comments,
        emailAddress: this.emailAddress,
        "Shipping Volume": this.orderVolume,
        hubspotCountry: (() => {
          const langTag = getCurrentLanguage().toUpperCase();
          return langTag === "EN" ? "GLOBAL" : langTag;
        })(),
        tool: "CN22/CN23 Customs Forms",
      };
    },
  },
  methods: {
    addItem() {
      this.items.push({
        quantity: 1,
        description: null,
        weight: 1,
        value: null,
        hsCode: null,
        originCountry: null,
      });
    },
    deleteItem(index) {
      if (this.items.length === 1) return;
      this.items.splice(index, 1);
    },
    goToStep(step) {
      if (step > 3) return;
      this.activeStepIndex = step;
    },
    goToDownloadForm() {
      this.activeStepIndex = 3;
    },
    enableCN23Form() {
      this.prefersCN23 = true;
      // this.$refs["fullName"].focus();
    },
    changeSummaryValue({ property, value }) {
      this[property] = value;
    },
    hideSignaturePlaceholder() {
      this.showSignaturePlaceholder = false;
    },
    saveSignature() {
      const { data } = this.$refs["signaturePad"].saveSignature();
      this.signatureData = data;
    },
    clearSignature() {
      this.$refs["signaturePad"].clearSignature();
      this.signatureData = null;
    },
    async downloadForm() {
      this.isGenerating = true;
      this.hasFormError = false;
      try {
        this.hasGeneratingError = false;

        if (this.$v.$invalid || !this.hasGivenConsent) {
          this.hasFormError = true;
          this.isGenerating = false;
          return;
        }

        //Post to Zapier
        await axios.post(
          "https://europe-west1-sendcloud-demos.cloudfunctions.net/zapier-proxy",
          this.formData
        );

        //Track Segment
        analytics.track("Customs Form Generated", this.formData); //eslint-disable-line

        //Generate PDF
        pdfMake
          .createPdf(createDocDefinition(this.formData))
          .download(
            `Customs Declaration ${
              this.invoiceNumber ||
              this.destination.businessName ||
              this.destination.fullName
            }.pdf`
          );
      } catch (error) {
        this.hasGeneratingError = true;
      }
      this.isGenerating = false;
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.cn23-view-wrap {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 100%;
  padding-bottom: 30px;
  background: rgba(255, 255, 255, 1);
  font-family: "Roboto", sans-serif;
}

.cn-23-form-wrap,
.step-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.signature-row {
  display: flex;
  margin: 40px 0;
  justify-content: center;
}

.signature-wrap {
  max-width: 500px;
}

.form-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 957px;
  min-height: 591px;
  margin: 0px;
  padding: 40px 0px 40px 0px;
  line-height: 1em;
}

.form-section {
  width: 100%;
}

.form-section-heading {
  text-align: center;
  font-weight: 600;
  font-size: 1.3em;
  margin: 20px 0px 20px 0px;
}

.form-section-disabled {
  opacity: 0.4;
}

.form-wrap:hover {
  margin: 0px;
}

.hs-code-button {
  height: 40px;
  margin: 0px 0px 25px 20px;
  padding: 10px;
  background: rgba(255, 255, 255, 0);
  border: 1px solid rgb(223, 223, 223);
  border-radius: 7px;
  transition: box-shadow 0.2s ease-in-out;
  font-weight: 500;
  font-size: 17px;
  line-height: 1em;
  flex-shrink: 1;
  color: #122857;
}

.hs-code-button:hover {
  box-shadow: 0px 3px 20px 0px rgba(0, 0, 0, 0.15);
}

.input-field {
  min-width: 0px;
  max-width: 100%;
  height: 40px;
  margin: 0px 0px 10px 0px;
  padding: 10px;
  font-family: inherit;
  font-size: 1em;
  color: #122857;
  background: rgb(240, 244, 246);
  border: 1px none black;
  border-radius: 5px;
  line-height: 1em;
}

.input-field.grow {
  flex-grow: 1;
}

.input-field:focus {
  border-bottom: 2px solid #122857;
  outline: none;
}

.input-field.country-select {
  width: 100%;
}

.input-field-unit {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 35px;
  height: 40px;
  margin: 0px 0px 10px 0px;
  padding: 5px;
  background: rgb(240, 244, 246);
  border-radius: 5px;
  font-size: 14px;
  line-height: 1em;
  color: #44565f;
  text-align: center;
}

.input-field-unit.after {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.input-field-with-unit {
  width: 100%;
  height: 40px;
  margin: 0px 0px 10px 0px;
  padding: 10px;
  background: rgb(240, 244, 246);
  border: 1px none black;
  border-radius: 5px;
  line-height: 1em;
}

.input-field-with-unit:focus {
  border-bottom: 2px solid #122857;
}

.input-field-wrap {
  display: flex;
  width: 100%;
  min-height: 19px;
  margin: 0px;
  padding: 0px;
  line-height: 1em;
}

.input-group {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: flex-start;
  width: 100%;
  min-height: 90px;
  margin: 0px 0px 0px 0px;
  padding: 0px;
  line-height: 1em;
}

.input-group-checkboxes {
  display: flex;
  flex-direction: row;
}

.input-checkbox-wrap {
  display: flex;
  align-items: center;
  margin-right: 14px;
}

.input-checkbox-wrap input {
  margin-right: 7px;
}

.input-group-half {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: flex-start;
  width: 50%;
  margin: 0px 0px 20px 0px;
  padding: 0px 10px;
  line-height: 1em;
}

.input-group-hs-button {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-self: flex-end;
  width: 100%;
  margin: 0px 0px 20px 0px;
  padding: 0px;
  line-height: 1em;
}

.input-group-padded {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 50%;
  min-height: 59px;
  margin: 0px 0px 20px 0px;
  padding: 0px 10px 0px 0px;
  line-height: 1em;
}

.signature-delete-button {
  right: 0px;
  top: 10px;
  position: absolute;
  width: 45px;
  min-height: 45px;
  margin: 0px;
  border: 1px solid #f1f1f1;
  color: #122857;
  border-radius: 34px;
  transition: background 0.25s ease-in-out;
  font-size: 18px;
  line-height: 1em;
}

.input-help-text {
  margin: 0px;
  font-size: 13px;
  line-height: 1.15em;
  color: #44565f;
}

.input-row {
  display: flex;
  margin: 0px -10px;
  align-self: stretch;
  line-height: 1em;
}

.input-row.second {
  margin-top: 20px;
}

.input-row-1 {
  position: absolute;
  display: flex;
  width: 270px;
  min-height: 71px;
  align-self: stretch;
  margin: 109px 0px 0px 25px;
  line-height: 1em;
}

.label {
  display: inline;
  margin: 0px 0px 10px 0px;
  font-weight: 700;
  font-size: 16px;
  line-height: 1em;
  color: #122857;
  white-space: nowrap;
}

.delete-button {
  right: -20px;
  top: -23px;
  position: absolute;
  width: 45px;
  min-height: 45px;
  margin: 0px;
  border: 1px solid #f1f1f1;
  color: #122857;
  border-radius: 34px;
  transition: background 0.25s ease-in-out;
  font-size: 18px;
  line-height: 1em;
}

.centered-checkbox-group {
  display: flex;
  justify-content: center;
  text-align: center;
}

.button {
  position: relative;
  width: 193.5px;
  min-height: 40px;
  margin: 20px 0px 0px 0px;
  background: rgb(18, 40, 87);
  border: 1px none black;
  border-radius: 5px;
  transition: background 0.25s ease-in-out;
  font-weight: 500;
  font-size: 19px;
  line-height: 1em;
  color: white;
}

.form-card-column-20 {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 20%;
  min-height: 224px;
  margin: 0px;
  padding: 25px 20px 25px 20px;
  line-height: 1em;
}

.form-card-column-40 {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 40%;
  min-height: 221px;
  margin: 0px;
  padding: 25px 20px 25px 20px;
  line-height: 1em;
}

.continue-button {
  position: relative;
  width: 193.5px;
  min-height: 40px;
  margin: 20px 0px 0px 0px;
  background: rgb(18, 40, 87);
  border: 1px none black;
  border-radius: 45px;
  transition: background 0.25s ease-in-out;
  font-weight: 500;
  font-size: 19px;
  line-height: 1em;
  text-align: center;
  padding: 7.5px 15px;
  color: white;
  transition: transform 0.15s;
}

.continue-button:active {
  background: rgb(23, 51, 112);
  transform: scale(1.05, 1.05);
}

.continue-button:focus {
  box-shadow: 0px 0px 2px 3px rgba(40, 91, 202, 0.3);
}

.continue-button:active,
.continue-button:focus {
  outline: none;
}

.continue-button svg {
  margin-right: 5px;
  font-size: 0.95em;
}

.alert {
  margin-bottom: 40px;
}

.signature-pad {
  background: rgb(240, 244, 246);
  border-radius: 5px;
}

.signature-pad-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
}

.signature-placeholder {
  position: absolute;
  opacity: 0.7;
  pointer-events: none;
}

.pdf-parent-wrap {
  position: relative;
  width: 100%;
}

.disclaimer-text {
  margin-top: 45px;
  max-width: 100%;
}

.privacy-policy-link {
  color: #1d97ff;
}
</style>
